import { TradeType } from 'app/console-api';
import { ObjectSchema, object, string, array, number } from 'yup';

/**
 * Trade Type Configuration
 * Centralized configuration for trade type specific behavior
 */

export interface TradeTypeFeatures {
  requiresMyOffer: boolean;
  showCustomMetadata: boolean;
  showCategories: boolean;
  showRewards: boolean;
  showAutomations: boolean;
  allowsTargetOfferSelection: boolean;
}

export interface TradeTypeUIConfig {
  features: TradeTypeFeatures;
  titleTemplate: (targetAppName: string) => string;
  myOfferLabel: string;
  myOfferDescription: string;
}

/**
 * Configuration map for each trade type
 */
export const TRADE_TYPE_CONFIG: Record<TradeType, TradeTypeUIConfig> = {
  exchange: {
    features: {
      requiresMyOffer: true,
      showCustomMetadata: true,
      showCategories: true,
      showRewards: true,
      showAutomations: true,
      allowsTargetOfferSelection: true
    },
    titleTemplate: (targetAppName: string) => `Exchange with ${targetAppName}`,
    myOfferLabel: 'My Offer',
    myOfferDescription: 'Your offer for traffic exchange that will be displayed on the partner\'s app.'
  },
  buy: {
    features: {
      requiresMyOffer: true,
      showCustomMetadata: false,
      showCategories: false,
      showRewards: false,
      showAutomations: true,
      allowsTargetOfferSelection: true
    },
    titleTemplate: (targetAppName: string) => `Buy traffic from ${targetAppName}`,
    myOfferLabel: 'My Offer',
    myOfferDescription: 'Your offer that will be displayed to the traffic seller.'
  },
  sell: {
    features: {
      requiresMyOffer: false,
      showCustomMetadata: true,
      showCategories: true,
      showRewards: true,
      showAutomations: true,
      allowsTargetOfferSelection: false
    },
    titleTemplate: (targetAppName: string) => `Sell traffic to ${targetAppName}`,
    myOfferLabel: '',
    myOfferDescription: ''
  },
  internal: {
    features: {
      requiresMyOffer: false,
      showCustomMetadata: true,
      showCategories: true,
      showRewards: true,
      showAutomations: true,
      allowsTargetOfferSelection: false
    },
    titleTemplate: (targetAppName: string) => `Internal task for ${targetAppName}`,
    myOfferLabel: '',
    myOfferDescription: ''
  }
};

/**
 * Get configuration for a specific trade type
 */
export const getTradeTypeConfig = (tradeType: TradeType): TradeTypeUIConfig => {
  return TRADE_TYPE_CONFIG[tradeType];
};

/**
 * Check if a feature is enabled for a trade type
 */
export const isFeatureEnabled = (
  tradeType: TradeType,
  feature: keyof TradeTypeFeatures
): boolean => {
  return TRADE_TYPE_CONFIG[tradeType].features[feature];
};

/**
 * Generate default title for a deal based on trade type
 */
export const generateDealTitle = (tradeType: TradeType, targetAppName: string): string => {
  return TRADE_TYPE_CONFIG[tradeType].titleTemplate(targetAppName);
};

export const buildDealValidationSchema = (tradeType: TradeType, options?: { isUpdate?: boolean }) => {
  const config = getTradeTypeConfig(tradeType);
  const schema: any = {
    title: string()
      .required('Deal title is required')
      .max(100, 'Deal title must be at most 100 characters')
  };

  // Only include myApp and myOffer for create/request forms, not update forms
  if (!options?.isUpdate) {
    schema.myApp = object().shape({
      label: string().required('At least one App should be selected'),
      value: string().required('At least one App should be selected'),
      type: string().required('At least one App should be selected')
    });

    // My Offer - conditionally required
    if (config.features.requiresMyOffer) {
      schema.myOffer = object().shape({
        label: string().required('At least one Task should be selected'),
        value: string().required('At least one Task should be selected')
      });
    } else {
      schema.myOffer = object().shape({
        label: string().optional(),
        value: string().optional()
      }).nullable().optional();
    }
  }

  // Custom Metadata
  if (config.features.showCustomMetadata) {
    schema.customMetadata = string().max(500, 'Metadata must be at most 500 characters');
  }

  // Rewards
  if (config.features.showRewards) {
    schema.rewards = array()
      .of(
        object().shape({
          rewardID: number(),
          value: number()
            .required('Reward value is required')
            .min(1, 'Reward value must be greater than 0')
        })
      )
      .test(
        'unique-types',
        'Reward types must be unique. You have duplicate reward types.',
        (rewards) => {
          if (!Array.isArray(rewards)) return true;
          const types = rewards.map((r: any) => r.rewardID);
          return new Set(types).size === types.length;
        }
      );
  }

  // Categories
  if (config.features.showCategories) {
    schema.categories = array().of(number().required()).min(1, 'At least one category must be selected');
  }

  return object().shape(schema);
};
